import { v4 as uuidv4 } from 'uuid';
import type { SigningField } from '../types/fields';
import { clampFieldToPage } from './coordinates';
import { mediaRectToVisualBox, visualPageDimensions, visualRectToMediaBox } from './pageRotation';

/**
 * After a page mutation, map each field's pageIndex using pageIndexMap[oldIndex] = newIndex.
 * Entries of -1 mean the page was removed — those fields are dropped.
 */
export function remapFieldPageIndices(
  fields: SigningField[],
  pageIndexMap: number[]
): SigningField[] {
  const next: SigningField[] = [];
  for (const field of fields) {
    const mapped = pageIndexMap[field.pageIndex];
    if (mapped === undefined || mapped < 0) continue;
    next.push({ ...field, pageIndex: mapped });
  }
  return next;
}

/**
 * After duplicating a page, shift existing fields via pageIndexMap and clone every
 * field on the duplicated page onto the new page at duplicatedPageIndex + 1.
 */
export function remapFieldsOnPageDuplicate(
  fields: SigningField[],
  duplicatedPageIndex: number,
  pageIndexMap: number[],
  createId: () => string = uuidv4
): SigningField[] {
  const remapped = remapFieldPageIndices(fields, pageIndexMap);
  const clones: SigningField[] = [];
  for (const field of fields) {
    if (field.pageIndex !== duplicatedPageIndex) continue;
    clones.push({
      ...field,
      id: createId(),
      pageIndex: duplicatedPageIndex + 1,
    });
  }
  return [...remapped, ...clones];
}

/**
 * After a page reorder, remap each field's pageIndex so it follows its page.
 *
 * The 'order' array encodes the new sequence as: order[newPosition] = oldIndex.
 * We invert it to build oldIndex -> newIndex, then update each field.
 *
 * Example: order = [2, 0, 1] means:
 *   new position 0 <-- old page 2
 *   new position 1 <-- old page 0
 *   new position 2 <-- old page 1
 * So old page 0 -> new position 1, old page 1 -> new position 2, old page 2 -> new position 0.
 */
export function remapFieldsOnReorder(
  fields: SigningField[],
  order: number[]
): SigningField[] {
  // Build old->new map by inverting the order array
  const oldToNew = new Map<number, number>();
  for (let newPos = 0; newPos < order.length; newPos++) {
    oldToNew.set(order[newPos], newPos);
  }

  const next: SigningField[] = [];
  for (const field of fields) {
    const newIndex = oldToNew.get(field.pageIndex);
    // Fields on pages not in the order array (shouldn't happen) are kept as-is
    if (newIndex === undefined) {
      next.push(field);
    } else {
      next.push({ ...field, pageIndex: newIndex });
    }
  }
  return next;
}

/**
 * When a page's /Rotate changes, keep fields anchored in media space and re-express in visual space.
 */
export function remapFieldsOnPageRotation(
  fields: SigningField[],
  pageIndex: number,
  mediaWidth: number,
  mediaHeight: number,
  oldRotation: number,
  newRotation: number
): SigningField[] {
  return fields.map((field) => {
    if (field.pageIndex !== pageIndex) return field;
    const media = visualRectToMediaBox(
      { x: field.x, y: field.y, width: field.width, height: field.height },
      mediaWidth,
      mediaHeight,
      oldRotation
    );
    const visual = mediaRectToVisualBox(media, mediaWidth, mediaHeight, newRotation);
    const { width: visualWidth, height: visualHeight } = visualPageDimensions(
      mediaWidth,
      mediaHeight,
      newRotation
    );
    const clamped = clampFieldToPage(
      {
        ...field,
        x: visual.x,
        y: visual.y,
        width: visual.width,
        height: visual.height,
      },
      visualWidth,
      visualHeight
    );

    // Ink strokes store absolute visual-space coordinates (same space as x/y).
    // The box was just re-expressed for the new rotation, so each point must be
    // too — otherwise strokes keep the OLD rotation's coordinates and land in
    // the wrong place. Re-express via old→media→new, then apply the same origin
    // shift the clamp applied to the box so the stroke stays registered with it.
    if (!field.points || field.points.length === 0) return clamped;
    const shiftX = clamped.x - visual.x;
    const shiftY = clamped.y - visual.y;
    const points = field.points.map((pt) => {
      const ptMedia = visualRectToMediaBox(
        { x: pt.x, y: pt.y, width: 0, height: 0 },
        mediaWidth,
        mediaHeight,
        oldRotation
      );
      const ptVisual = mediaRectToVisualBox(ptMedia, mediaWidth, mediaHeight, newRotation);
      return { x: ptVisual.x + shiftX, y: ptVisual.y + shiftY };
    });
    return { ...clamped, points };
  });
}